import React from 'react'

import { MapPinPlusIcon, Trash2Icon } from 'lucide-react'
import { observer } from 'mobx-react'

import { ITileset } from '~/client/graph'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '~/client/src/shared/components/shadcn-ui/field'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import { Textarea } from '~/client/src/shared/components/shadcn-ui/textarea'
import {
  ToggleGroup,
  ToggleGroupItem,
} from '~/client/src/shared/components/shadcn-ui/toggle-group'
import { useCreatableLocationKinds } from '~/client/src/shared/hooks/useCreatableLocationKinds'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ICreatableLocationKinds } from '~/client/src/shared/resources/domain/creatableLocationKinds'
import { compareLabelsAlphanumeric } from '~/client/src/shared/utils/compareGroupsByLabel'
import { isSelfHostedTilesetId } from '~/client/src/shared/utils/selfHostedTileset'

import {
  creatableLocationObject,
  getLocationObjectIcon,
  ILocationListObject,
  informationPointTypeObjects,
  locationObjectKey,
  locationObjects,
} from '../../views/ProjectSetUp/components/AppsSitemap/components/LocationObjectsPallet/locationObjectsHelper'
import MapViewSetUpStore from '../../views/ProjectSetUp/components/AppsSitemap/MapViewSetUp.store'

interface IProps {
  store: MapViewSetUpStore
}

// A root that creates something none of its children do (the generic Vertical, Equipment or
// Logistics object) is pickable too, as it is in the location pallet; it heads its own submenu.
const isRootDistinct = (object: ILocationListObject): boolean =>
  !object.children.some(
    child => locationObjectKey(child) === locationObjectKey(object),
  )

const creatableMenuObject = (
  object: ILocationListObject,
  isCreatable: ICreatableLocationKinds['isCreatable'],
): ILocationListObject | null => {
  const entry = creatableLocationObject(object, isCreatable)
  return entry?.children && !entry.isDisabled && isRootDistinct(object)
    ? {
        ...entry,
        children: [{ ...object, children: undefined }, ...entry.children],
      }
    : entry
}

// Text stickers and lines are sitemap items, not locations: only entries with a location type
// can be created from a shape, and only those the project can create. A shape has no picker to
// open, so Information Points list their types, A to Z like Place a point. Built at render: the
// labels are read in the current language.
export const creatableLocationSections = (
  isCreatable: ICreatableLocationKinds['isCreatable'],
): ILocationListObject[][] =>
  locationObjects
    .map(section =>
      section
        .filter(object => object.dataObjectType)
        .map(object =>
          creatableMenuObject(
            object.opensInformationPointPicker
              ? {
                  ...object,
                  children: [...informationPointTypeObjects].sort((a, b) =>
                    compareLabelsAlphanumeric(a.getLabel(), b.getLabel()),
                  ),
                }
              : object,
            isCreatable,
          ),
        )
        .filter(Boolean),
    )
    .filter(section => section.length)

function ReadOnlyField({
  id,
  label,
  value,
  type,
}: {
  id: string
  label: string
  value: string
  type?: 'number'
}): JSX.Element {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input id={id} type={type} value={value ?? ''} disabled />
    </Field>
  )
}

function LocationObjectMenuItem({
  object,
  onSelect,
}: {
  object: ILocationListObject
  onSelect: (object: ILocationListObject) => void
}): JSX.Element {
  const Icon = getLocationObjectIcon(object)

  if (object.children) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Icon />
          {object.getLabel()}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {object.children.map(child => (
            <LocationObjectMenuItem
              key={locationObjectKey(child)}
              object={child}
              onSelect={onSelect}
            />
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    )
  }

  return (
    <DropdownMenuItem onClick={() => onSelect(object)}>
      <Icon />
      {object.getLabel()}
    </DropdownMenuItem>
  )
}

const ShapeProperties = observer(function ShapeProperties({
  store,
}: IProps): JSX.Element {
  const {
    selectedTilesetFeature,
    selectedTilesetFeatures,
    selectedTilesetFeaturePartsCount,
    setSelectedTilesetFeature,
  } = store.mapBoxViewerStore.tilesetsControlStore
  const { isReady, isCreatable } = useCreatableLocationKinds()
  if (!selectedTilesetFeature) {
    return null
  }

  const features = selectedTilesetFeatures.slice().reverse()

  // The icon names the new location ("Crane" rather than its raw type) and is what the marker draws.
  // A root entry also carries its kind: the Vertical Object root shares its icon with Stairway.
  const createLocation = (object: ILocationListObject): void =>
    store.mapViewItemsSetupStore.createAttributeFromTilesetPolyshape(
      object.dataObjectType,
      object.iconName,
      object.kind,
    )

  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:p-2.5">
      <h2 className="tw:m-0 tw:text-app-subhead tw:text-foreground">
        {Localization.translator.shapeProperties}
      </h2>
      {features.length > 1 && (
        <ToggleGroup
          variant="outline"
          size="sm"
          spacing={0}
          className="tw:w-full"
          value={[String(selectedTilesetFeature.id)]}
          onValueChange={([id]) => {
            const feature = features.find(f => String(f.id) === id)
            if (feature) {
              setSelectedTilesetFeature(feature)
            }
          }}
        >
          {features.map((feature, idx) => (
            <ToggleGroupItem
              key={feature.id}
              value={String(feature.id)}
              className="tw:flex-1"
            >
              {idx + 1}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="primary-outline" />}
          disabled={!isReady}
        >
          <MapPinPlusIcon data-icon="inline-start" />
          {Localization.translator.createLocationWithThisShape}
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {creatableLocationSections(isCreatable).map((section, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <DropdownMenuSeparator />}
              <DropdownMenuGroup>
                {section.map(object => (
                  <LocationObjectMenuItem
                    key={locationObjectKey(object)}
                    object={object}
                    onSelect={createLocation}
                  />
                ))}
              </DropdownMenuGroup>
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <FieldGroup>
        <ReadOnlyField
          id="tileset-feature-geometry-type"
          label={Localization.translator.geometryType}
          value={selectedTilesetFeature.geometry?.type}
        />
        {selectedTilesetFeaturePartsCount > 1 && (
          <ReadOnlyField
            id="tileset-feature-parts"
            label={Localization.translator.parts}
            value={selectedTilesetFeaturePartsCount.toString()}
            type="number"
          />
        )}
        {Object.entries(selectedTilesetFeature.properties).map(
          ([key, value], idx) => (
            <ReadOnlyField
              key={`tileset-feature-property-${idx}`}
              id={`tileset-feature-property-${idx}`}
              label={key}
              value={value?.toString()}
            />
          ),
        )}
      </FieldGroup>
    </div>
  )
})

const FileProperties = observer(function FileProperties({
  store,
  tileset,
}: IProps & { tileset: ITileset }): JSX.Element {
  const { tilesetsSetupStore } = store

  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:p-2.5">
      <div className="tw:flex tw:items-center tw:justify-between">
        <h2 className="tw:m-0 tw:text-app-subhead tw:text-foreground">
          {Localization.translator.fileProperties}
        </h2>
        <Button
          variant="destructive"
          size="icon"
          aria-label={Localization.translator.delete}
          onClick={() =>
            tilesetsSetupStore.showDeleteConfirmationDialogByTileset(tileset)
          }
        >
          <Trash2Icon />
        </Button>
      </div>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="tileset-name">
            {Localization.translator.name}
          </FieldLabel>
          <Textarea
            id="tileset-name"
            value={tileset.mapboxData.name ?? ''}
            disabled
          />
        </Field>
        {!isSelfHostedTilesetId(tileset.mapboxTilesetId) && (
          <>
            <ReadOnlyField
              id="tileset-latitude"
              label={Localization.translator.latitude}
              value={tileset.mapboxData.center?.[1]?.toString()}
              type="number"
            />
            <ReadOnlyField
              id="tileset-longitude"
              label={Localization.translator.longitude}
              value={tileset.mapboxData.center?.[0]?.toString()}
              type="number"
            />
            <ReadOnlyField
              id="tileset-zoom"
              label={Localization.translator.zoom}
              value={tileset.mapboxData.center?.[2]?.toString()}
              type="number"
            />
          </>
        )}
      </FieldGroup>
    </div>
  )
})

function TilesetProperties({ store }: IProps): JSX.Element {
  const tileset =
    store.mapBoxViewerStore.tilesetsControlStore.selectedFeatureTileset ||
    store.tilesetsSetupStore.selectedTileset

  return (
    <div className="tw:h-full tw:overflow-x-hidden tw:overflow-y-auto">
      <div className="tw:flex tw:flex-col tw:divide-y tw:divide-border tw:py-2.5">
        {tileset && (
          <>
            <h1
              className="tw:mt-0 tw:mb-3 tw:px-2.5 tw:text-center tw:text-app-section-title tw:line-clamp-2 tw:break-words tw:text-foreground"
              title={tileset.mapboxData.name}
            >
              {tileset.mapboxData.name}
            </h1>
            <FileProperties store={store} tileset={tileset} />
          </>
        )}
        <ShapeProperties store={store} />
      </div>
    </div>
  )
}

export default observer(TilesetProperties)
